import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import { AxiosError } from "axios"
import { formatDuration, intervalToDuration, type Duration } from "date-fns"
import domtoimage from "dom-to-image"
import FileSaver from "file-saver"
import JSZip from "jszip"
import moment, { Moment } from "moment"

import { DateTimeFormatOptions, FormattedDurationLog } from "../types/Custom/Interfaces"
import { ColoredLineGraphData, ServiceEventType, TableColumn } from "../types/Custom/Types"
import { definitions } from "../types/Generated/apiTypes"
import { getHexColorFromCSSVariable, getSequentialBorderColor } from "./drawingUtils"

type NodeDownTimeLog = definitions["DailyDownTimeLog"]
type CameraDownTimeLog = definitions["CameraDownTimeLog"]
type Features = definitions["Features"]

export const adjustDate = (date: moment.Moment, time: "start" | "end"): moment.Moment => {
  /**
   * utcOffset(0) to standardize on UTC, and local() on the date before passing it to the date picker.
   * This will convert it back to the user's local time zone for display.
   */
  const adjustedDate = date.utcOffset(0).local()
  if (time === "start") {
    adjustedDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
  } else {
    adjustedDate.set({ hour: 23, minute: 59, second: 59, millisecond: 999 })
  }
  return adjustedDate
}

/**
 * Returns true if start and end dates are in two different calendar months (or years).
 * Used to show the time grain (month) switch when the date range spans more than one month.
 * @param start - Start date (Moment, Date, or ISO string)
 * @param end - End date (Moment, Date, or ISO string)
 */
export const areDatesInDifferentMonths = (
  start?: Moment | Date | string | null,
  end?: Moment | Date | string | null
): boolean => {
  if (start == null || end == null) return false
  const startDate = moment.isMoment(start) ? start.toDate() : typeof start === "string" ? new Date(start) : start
  const endDate = moment.isMoment(end) ? end.toDate() : typeof end === "string" ? new Date(end) : end
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return false
  return startDate.getFullYear() !== endDate.getFullYear() || startDate.getMonth() !== endDate.getMonth()
}

/**
 * Function to extract the page number from pagination backend link
 * @param {string} link - The pagination link
 * @returns {number | undefined} - The extracted page number or undefined if not found
 */
export const extractPageFromBackEndPaginationLink = (link: string): number | undefined => {
  // Check if the link contains '?'
  const hasQueryParam = link.includes("?")

  if (!hasQueryParam) {
    // '?' character not found, return undefined
    return undefined
  }

  // Extract the query parameters from the URL
  const queryString = link.split("?")[1]
  const params = new URLSearchParams(queryString)

  // Get the value of the 'page' parameter
  const pageParam = params.get("page")

  // Parse and return the page number
  return pageParam ? parseInt(pageParam) : undefined // Return undefined if 'page' parameter is not found
}

/**
 * Parses error data received from Axios response into an array of error messages.
 * @param data Error data object received from Axios response.
 * @returns Array of error messages formatted as `${key}: ${value}`.
 */
export const parseErrorMessages = (data: any): string[] => {
  return Object.entries(data).map(([key, value]: [string, any]) => `${key}: ${value}`)
}

/**
 * Returns a default error message based on the HTTP status code.
 * @param statusCode The HTTP status code
 * @returns A user-friendly error message
 */
const getDefaultErrorMessage = (statusCode: string | number): string => {
  const statusMessages: { [key: string]: string } = {
    "400": "Bad Request",
    "401": "Unauthorized",
    "403": "Forbidden",
    "404": "Not Found",
    "405": "Method Not Allowed",
    "408": "Request Timeout",
    "409": "Conflict",
    "413": "Payload Too Large",
    "415": "Unsupported Media Type",
    "429": "Too Many Requests",
    "500": "Internal Server Error",
    "501": "Not Implemented",
    "502": "Bad Gateway",
    "503": "Service Unavailable",
    "504": "Gateway Timeout",
  }

  // Validate status code
  const code = typeof statusCode === "number" ? statusCode.toString() : statusCode
  // Check if code is a valid 3-digit HTTP status code (e.g. 200, 404, 500)
  // The regex /^\d{3}$/ ensures:
  // ^ - start of string
  // \d{3} - exactly 3 digits
  // $ - end of string
  if (!code || !/^\d{3}$/.test(code)) {
    return "An unexpected error occurred"
  }

  return statusMessages[code] || `Error ${code}: An unexpected error occurred`
}

/**
 * Extracts error code from HTML response or falls back to HTTP status code.
 * @param responseData The response data that might contain HTML
 * @param statusCode The HTTP status code as fallback
 * @returns The extracted error code or fallback status code, or null if no error code can be determined
 */
const extractErrorCode = (responseData: string, statusCode?: number): string | number | null => {
  const errorCodeMatch = responseData.match(/<div[^>]*id="error-code"[^>]*>(\d+)<\/div>/)
  return errorCodeMatch ? errorCodeMatch[1] : statusCode || null
}

/**
 * Displays error notifications for Axios errors, providing details based on the operation type.
 * @param error AxiosError object containing error details.
 * @param operationType Specifies whether the error is from a 'query' or 'mutation'.
 */
export const displayAxiosErrorNotification = (error: AxiosError, operationType: "query" | "mutation") => {
  const defaultMessage = operationType === "mutation" ? "An error occurred. Please try again." : "An error occurred."

  // Handle case where there's no response data
  if (!error.response?.data) {
    NotificationUtils.toast(defaultMessage, { severity: "error" })
    return
  }

  // Handle HTML response
  if (typeof error.response.data === "string" && error.response.data.includes("<!DOCTYPE html>")) {
    const errorCode = extractErrorCode(error.response.data, error.response.status)

    // If we can't determine the error code, show a generic message
    if (!errorCode) {
      NotificationUtils.toast("An unexpected error occurred", { severity: "error" })
      return
    }

    const errorMessage = getDefaultErrorMessage(errorCode)
    NotificationUtils.toast(`${errorCode}: ${errorMessage}`, { severity: "error" })
    return
  }

  // Handle JSON response
  const errorMessages = parseErrorMessages(error.response.data)
  NotificationUtils.toast(errorMessages.join("\n"), { severity: "error" })
}

/**
 * Extracts uppercase initials from a full name. If a last name is provided,
 * it uses the first character of the first name and last name. If no last
 * name, it takes the first two characters of the first name (if a space exists)
 * or the first two characters of the first name.
 *
 * @param {string} name - The full name.
 * @returns {string} Uppercase initials.
 */
export const getNameInitials = (name?: string): string => {
  if (!name) return ""
  const trimmedName = name.trim()
  if (trimmedName.includes(" ")) {
    return trimmedName
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
  } else {
    return name.slice(0, 2).toUpperCase()
  }
}

interface Log {
  [key: string]: any // Allow any additional properties
  color?: string // The color property is optional
}

export function sortAndColorizeLogs(data: Log[], countKey: keyof Log): Log[] {
  if (data && data.length > 0) {
    // Sort the array by the 'count' property in descending order
    data.sort((a, b) => (b[countKey] as number) - (a[countKey] as number))

    // Set the color property based on the index in the sorted array
    data.forEach((log, index) => {
      log.color = index === 0 ? "var(--primary-background-default)" : "var(--blue-background-2)"
    })
  }

  return data
}

/**
 * Converts raw data into a format suitable for a colored line graph.
 *
 * @param {Array<Object>} data - The raw data to be converted.
 * @param {string} logsKey - The key in the data object that contains the log entries.
 * @param {string} indexByKey - The key in the data object that contains the log entries are indexed by.
 * @param {string} logTimeStampKey - The key in the log entries that contains the timestamp.
 * @param {string} logCountKey - The key in the log entries that contains the count value.
 * @param {string} timeGrain - The time grain of data = hour or day (Optional).
 * @param {string} valueUnit - The unit of the input values ("seconds" or "minutes"). If provided, conversion will be applied.
 * @param {string} targetUnit - The target unit to convert to ("seconds" or "minutes"). Defaults to "minutes" if valueUnit is provided.
 * @returns {Array<{ id: string, color: string, data: Array<{ x: Date, y: number }> }>}
 * An array of objects formatted for a colored line graph.
 */

/**
 * Parses hourly time formats and returns a Date object.
 * Handles two formats: "05:00" (HH:mm string) or "5" (number string).
 * @param timeValue - The time value as a string ("05:00" or "5")
 * @returns A Date object with the time set, or null if parsing fails
 */
export function parseHourlyTime(timeValue: string): Date | null {
  if (typeof timeValue !== "string") {
    return null
  }

  // Handle empty string
  if (timeValue.trim() === "") {
    return null
  }

  // Explicitly reject "24:00" format (moment accepts it as valid, but we don't want it)
  if (timeValue === "24:00" || timeValue.startsWith("24:")) {
    return null
  }

  // Try to parse as "HH:mm" format (e.g., "05:00")
  const timeMoment = moment(timeValue, "HH:mm", true)
  if (timeMoment.isValid()) {
    const hour = timeMoment.hour()
    const minutes = timeMoment.minutes()
    // Validate that hour is in valid range (0-23) and minutes are 0
    if (hour >= 0 && hour <= 23 && minutes === 0) {
      return timeMoment.toDate()
    }
  }

  // Try to parse as a number string (e.g., "5")
  const hourNumber = parseInt(timeValue, 10)
  if (!Number.isNaN(hourNumber) && hourNumber >= 0 && hourNumber <= 23) {
    const date = new Date()
    date.setHours(hourNumber, 0, 0, 0)
    return date
  }

  return null
}

/**
 * Normalizes a date to the first day of its month at midnight.
 * Used for month time grain to ensure all dates in the same month have the same x-coordinate.
 * @param date - The date to normalize
 * @returns A new Date object set to the first day of the month at 00:00:00
 */
export function normalizeToFirstDayOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0)
}

/**
 * Parses a month timestamp value and normalizes it to the first day of the month.
 * Handles both "YYYY-MM" format and full date strings.
 * @param timeStampValue - The timestamp value as a string
 * @returns A Date object normalized to the first day of the month at midnight
 */
export function parseMonthTimestamp(timeStampValue: string): Date {
  // Try to parse as "YYYY-MM" format first
  const monthMoment = moment(timeStampValue, "YYYY-MM", true)
  if (monthMoment.isValid()) {
    return monthMoment.startOf("month").toDate()
  }

  // Fallback: parse as date and normalize to first day of month
  const parsedDate = new Date(timeStampValue)
  if (!Number.isNaN(parsedDate.getTime())) {
    return normalizeToFirstDayOfMonth(parsedDate)
  }

  // Final fallback: return parsed date as-is (should rarely happen)
  return new Date(timeStampValue)
}

/**
 * Parses a timestamp value based on the time grain, normalizing dates appropriately.
 * For month time grain, normalizes dates to the first day of the month at midnight.
 * @param timeStampValue - The timestamp value (string, number, or Date)
 * @param timeGrain - The time grain ("hour", "day", "month", or null)
 * @returns A Date object representing the parsed timestamp
 */
export function parseTimestampByTimeGrain(
  timeStampValue: string | number | Date,
  timeGrain?: "hour" | "day" | "month" | null
): Date {
  // Handle numeric hour values (legacy format: 0-23)
  if (typeof timeStampValue === "number") {
    const date = new Date()
    date.setHours(timeStampValue, 0, 0, 0)
    return date
  }

  // Handle string values based on time grain
  if (typeof timeStampValue === "string") {
    if (timeGrain === "hour") {
      // Hourly data: parse string formats ("05:00" or "5")
      return parseHourlyTime(timeStampValue) || new Date(timeStampValue)
    }

    if (timeGrain === "month") {
      // Month data: normalize to first day of month at midnight
      return parseMonthTimestamp(timeStampValue)
    }
  }

  // Day data or fallback: parse as date string
  return new Date(timeStampValue)
}

/**
 * Formats hourly time formats into a readable string.
 * Handles formats: "05:00" (HH:mm string), "5" (number string), or 5 (number).
 * @param timeValue - The time value as a string ("05:00" or "5") or number (0-23)
 * @param format - The moment format string to use (e.g., "h A")
 * @returns A formatted time string
 */
export function formatHourlyTime(timeValue: string | number, format: string): string {
  // Handle number input (e.g., 5 or 22)
  if (typeof timeValue === "number") {
    if (timeValue >= 0 && timeValue <= 23) {
      return moment().hour(timeValue).minute(0).second(0).millisecond(0).format(format)
    }
    return moment().format(format)
  }

  // Handle string input
  // Try to parse as "HH:mm" format (e.g., "05:00")
  const timeMoment = moment(timeValue, "HH:mm", true)
  if (timeMoment.isValid()) {
    return timeMoment.format(format)
  }

  // Try to parse as a number string (e.g., "5")
  const hourNumber = parseInt(timeValue, 10)
  if (!Number.isNaN(hourNumber) && hourNumber >= 0 && hourNumber <= 23) {
    return moment().hour(hourNumber).minute(0).second(0).millisecond(0).format(format)
  }

  // Fallback: try to parse with moment directly
  return moment(timeValue).format(format)
}

/**
 * Transforms input data into graph and table formats suitable for colored line graphs and corresponding tables.
 * Handles flexible log and timestamp keys, supports unit conversion, and custom formatting for different time grains.
 *
 * @param {Object} params - The function parameters.
 * @param {Array<Object>} [params.data] - The input data array. Each item represents a series (e.g., a day, gate, etc.).
 * @param {string} params.logsKey - The key in each series item for the array of log points (e.g., 'hourly_count').
 * @param {string} params.indexByKey - The key in each series item to use as the index (e.g., 'day', 'entity').
 * @param {string} params.logTimeStampKey - The key within each log point for the timestamp (e.g., 'hour').
 * @param {string} params.logCountKey - The key within each log point for the value/count (e.g., 'avg_count').
 * @param {string} [params.timestampFormatting="h A"] - Moment.js string to format timestamps (for graph/table display).
 * @param {"hour" | "day" | "month" | null} [params.timeGrain] - If provided, determines the granularity for timestamp parsing/formatting.
 * @param {"seconds" | "minutes"} [params.valueUnit] - The unit of the values in the input data (applies to logCountKey).
 * @param {"seconds" | "minutes"} [params.targetUnit] - The desired unit for output values.
 *
 * @returns {Object} result
 * @returns {Array<{ id: string, color: string, data: Array<{ x: Date, y: number }> }>} result.graphData
 *          - Array of objects for colored line graphs. Each has an id, a color, and an array of points {x: Date, y: number}.
 * @returns {Array<Record<string, any>>} result.tableData
 *          - Array of plain objects for table rows, with formatted timestamp key and per-index values.
 *
 * @example
 * const { graphData, tableData } = convertDataToColoredLineGraphAndTable({
 *   data, logsKey: 'hourly_count', indexByKey: 'day',
 *   logTimeStampKey: 'hour', logCountKey: 'avg_count', timeGrain: 'hour'
 * });
 */
export function convertDataToColoredLineGraphAndTable({
  data,
  logsKey,
  indexByKey,
  logTimeStampKey,
  logCountKey,
  timestampFormatting = "h A", // Default timestamp formatting
  timeGrain,
  valueUnit,
  targetUnit,
}: {
  data?: Array<{ [key: string]: any }>
  logsKey: string
  indexByKey: string
  logTimeStampKey: string
  logCountKey: string
  timestampFormatting?: string
  timeGrain?: "hour" | "day" | "month" | null
  valueUnit?: "seconds" | "minutes"
  targetUnit?: "seconds" | "minutes"
}): {
  graphData: Array<{ id: string; color: string; data: Array<{ x: Date; y: number }> }>
  tableData: Record<string, any>[]
} {
  const graphData: Array<{ id: string; color: string; data: Array<{ x: Date; y: number }> }> = []
  const tableData: Record<string, any>[] = []

  if (!data || data.length < 1) return { graphData, tableData }

  // No need to clone - function only reads from data and creates new objects for output
  data.forEach((log, index) => {
    if (log[logsKey]) {
      const sequentialColorCSS = getSequentialBorderColor(index)
      // Extract CSS variable name from var(...) format: var(--blue-border-1) -> --blue-border-1
      const cssVariableName = sequentialColorCSS.replace(/^var\((.+)\)$/, "$1")
      const sequentialColor = getHexColorFromCSSVariable(cssVariableName) || sequentialColorCSS
      const logPointsArray: Array<{ x: Date; y: number }> = []

      log[logsKey].forEach((point: { [key: string]: any }) => {
        if (point[logCountKey] > 0) {
          const timeStampValue = point[logTimeStampKey]

          // Parse timestamp based on time grain
          const logTimeStamp = parseTimestampByTimeGrain(timeStampValue, timeGrain)

          // Apply unit conversion if valueUnit and targetUnit are provided
          let convertedValue = point[logCountKey]
          if (valueUnit && targetUnit && valueUnit !== targetUnit && typeof point[logCountKey] === "number") {
            if (valueUnit === "seconds" && targetUnit === "minutes") {
              convertedValue = Math.round(point[logCountKey] / 60)
            } else if (valueUnit === "minutes" && targetUnit === "seconds") {
              convertedValue = point[logCountKey] * 60
            }
          }

          logPointsArray.push({ x: logTimeStamp, y: convertedValue })

          // Format the readable index for table data
          let readableIndex: string
          if (timeGrain === "hour") {
            // For hourly data, convert to string if needed and format ("05:00" or "5")
            const timeString = typeof timeStampValue === "number" ? String(timeStampValue) : timeStampValue
            readableIndex = formatHourlyTime(timeString, timestampFormatting)
          } else {
            // For day/month data, parse the timestamp value
            readableIndex = moment(timeStampValue).format(timestampFormatting)
          }
          const existingEntry = tableData.find((entry) => entry[logTimeStampKey] === readableIndex)

          if (existingEntry) {
            existingEntry[log[indexByKey]] = convertedValue
          } else {
            tableData.push({
              [logTimeStampKey]: readableIndex,
              [log[indexByKey]]: convertedValue || 0,
            })
          }
        }
      })

      // Ensure that data logs are sorted in ascending hour order
      logPointsArray.sort((a, b) => a.x.getTime() - b.x.getTime())

      const formattedObj = {
        id: log[indexByKey],
        color: sequentialColor,
        data: logPointsArray,
      }

      graphData.push(formattedObj)
    }
  })

  // Sort tableData by the logTimeStampKey
  tableData.sort((a, b) => {
    const dateA = moment(a[logTimeStampKey], timestampFormatting).toDate()
    const dateB = moment(b[logTimeStampKey], timestampFormatting).toDate()
    return dateA.getTime() - dateB.getTime()
  })

  return { graphData, tableData }
}

/**
 * Generates a default checkbox state object from the provided graph data.
 *
 * This function takes an array of ColoredLineGraphData and returns an object where each key
 * is the ID of a graph data item and its value is a boolean (defaulting to false).
 *
 * @param {ColoredLineGraphData[]} data - The array of graph data from which to generate default checkbox states.
 * @returns {{ [key: string]: Boolean }} - An object where keys are graph data item IDs and values are false.
 */
export const getDefaultCheckboxes = (
  data: ColoredLineGraphData[]
): {
  [key: string]: Boolean
} => {
  if (!data || data?.length < 1) return { "": false }

  let statesObj: { [key: string]: Boolean } = {}
  // Loop through each item in graphData and set its default state to false
  for (let log of data) {
    statesObj[log.id] = false
  }
  return statesObj
}

/**
 * Calculates the appropriate date slice for a given date range.
 *
 * @param {Moment | null} startDate - The start date of the range. If `null`, the calculation will not proceed.
 * @param {Moment | null} endDate - The end date of the range. If `null`, the calculation will not proceed.
 * @returns {"hour" | "day" | undefined} - Returns "hour" if the difference between `endDate` and `startDate` is less than 1 day,
 *                                  otherwise returns "day". Returns `undefined` if either `startDate` or `endDate` is `null`.
 */
export const calculateDateSlice = (startDate: Moment | null, endDate: Moment | null): "hour" | "day" | undefined => {
  if (!!startDate && !!endDate) {
    const diff = endDate.diff(startDate, "days")
    return diff < 1 ? "hour" : "day"
  }
  return undefined
}

/**
 * Formats logs fetched from the backend to be in the form of actual date and duration in seconds.
 * Also sorts the logs by date and highlights the first day with a special color.
 *
 * @param {NodeDownTimeLog[]|CameraDownTimeLog[]} data - Object containing total down time and array of daily logs.
 * @param {string} dateFormat - Format of the date string, default is "DD MMM".
 * @returns {FormattedDurationLog[]} - Array of formatted logs with date, duration, and color.
 */
export const formatDurationGraphData = (
  data: NodeDownTimeLog[] | CameraDownTimeLog[],
  dateFormat: string = "DD MMM"
): FormattedDurationLog[] => {
  const formattedLogs: FormattedDurationLog[] = []

  if (data) {
    // Format each log
    data.forEach((log) => {
      const formattedDate = moment(log.date).format(dateFormat)

      // Type narrowing to differentiate between NodeDownTimeLog and CameraDownTimeLog
      const formattedLog: FormattedDurationLog = {
        date: formattedDate,
        duration:
          "day_total_down_time" in log
            ? (log as NodeDownTimeLog).day_total_down_time
            : (log as CameraDownTimeLog).duration || 0, // Default to 0 if duration is missing
        color: "var(--blue-background-2)",
      }

      formattedLogs.push(formattedLog)
    })

    // Sort logs by date
    formattedLogs.sort((a, b) => {
      const dateA = moment(a.date, dateFormat)
      const dateB = moment(b.date, dateFormat)
      return dateA.diff(dateB)
    })

    // Assign colors
    if (formattedLogs.length > 0) {
      const maxDuration = Math.max(...formattedLogs.map((log) => log.duration))

      formattedLogs.forEach((log) => {
        log.color = log.duration === maxDuration ? "var(--red-background-1)" : "var(--red-background-2)"
      })
    }
  }

  return formattedLogs
}

/**
 * Converts a duration from seconds or minutes to a human-readable format.
 *
 * @param {Object} params - The parameters object.
 * @param {number|string} params.duration - The duration value.
 * @param {string} [params.unit="seconds"] - The unit of the input duration ("seconds" or "minutes").
 * @param {string} [params.format="long"] - The format style: "long" (e.g., "13 hours 15 minutes") or "short" (e.g., "13hrs 15mins").
 * @param {boolean} [params.includeSeconds=false] - Whether to include seconds in the output if they exist.
 * @returns {string} - The human-readable duration.
 */
export function convertToHumanReadableDuration({
  duration,
  unit = "seconds",
  format = "long",
  includeSeconds = false,
}: {
  duration: number | string
  unit?: "seconds" | "minutes"
  format?: "long" | "short"
  includeSeconds?: boolean
}): string {
  // Ensure duration is a number
  const value = typeof duration === "string" ? parseInt(duration, 10) : duration

  if (isNaN(value) || value <= 0) {
    return "No Data"
  }

  // Convert to seconds if input is in minutes
  const seconds = unit === "minutes" ? value * 60 : value

  // Convert seconds to a Duration object
  const durationObj = intervalToDuration({ start: 0, end: seconds * 1000 })

  // Format the duration based on format type
  if (format === "short") {
    const days = durationObj.days || 0
    const hours = durationObj.hours || 0
    const minutes = durationObj.minutes || 0
    const secs = durationObj.seconds || 0

    if (days === 0 && hours === 0 && minutes === 0 && (!includeSeconds || secs === 0)) {
      return "< 1 min"
    }

    const parts: string[] = []
    if (days > 0) {
      parts.push(`${days} ${days === 1 ? "day" : "days"}`)
    }
    if (hours > 0) {
      parts.push(`${hours} hrs`)
    }
    if (minutes >= 0) {
      parts.push(`${minutes} mins`)
    }
    if (includeSeconds && secs > 0) {
      parts.push(`${secs} secs`)
    }

    return parts.join(" ") || "< 1 min"
  } else {
    // Long format (default) - uses formatDuration from date-fns
    const formatArray: Array<"hours" | "minutes" | "seconds"> = includeSeconds
      ? ["hours", "minutes", "seconds"]
      : ["hours", "minutes"]
    const formatted = formatDuration(durationObj, { format: formatArray })
    return formatted || "less than a minute"
  }
}

/**
 * Generic function to process visit data for graph visualization.
 * Can handle both car visit data (CarDailyVisitsDuration) and profile visit data (DayVisits).
 * Formats dates and assigns colors based on duration/time spent values.
 * Converts all duration values to seconds internally for consistency in comparison and coloring.
 *
 * @param {Array} visitData - Array of visit objects (CarDailyVisitsDuration[] or DayVisits[]).
 * @param {string} durationField - The field name for duration/time spent ("duration" for cars, "total_time_spent" for profiles).
 * @param {string} dateField - The field name for date ("date" for cars, "day" for profiles).
 * @param {string} timeUnit - The unit of the duration values ("seconds" or "minutes").
 * @returns {Array} - Formatted data with dates and colors for graph visualization.
 */
export function processVisitsData<T extends { [key: string]: any }>(
  visitData?: T[],
  durationField: keyof T = "duration" as keyof T,
  dateField: keyof T = "date" as keyof T,
  timeUnit: "seconds" | "minutes" = "seconds"
): (T & { formattedDate: string; color: string })[] {
  if (!visitData || visitData.length < 1) {
    return []
  }

  // Find max duration/time spent without sorting data (convert to seconds for comparison)
  let maxValue = 0
  visitData.forEach((visit) => {
    const value = visit[durationField]
    if (value && typeof value === "number") {
      const valueInSeconds = timeUnit === "minutes" ? value * 60 : value
      if (valueInSeconds > maxValue) {
        maxValue = valueInSeconds
      }
    }
  })

  // Format the data
  return visitData.map((visit) => {
    const dateValue = visit[dateField]
    const durationValue = visit[durationField]

    // Format date based on the field type
    let formattedDate: string
    if (typeof dateValue === "string") {
      // Handle both date formats (YYYY-MM-DD and ISO strings)
      if (dateValue.includes("T") || dateValue.includes("Z")) {
        // ISO date string
        formattedDate = moment(dateValue).format("DD MMM, YYYY")
      } else {
        // Simple date string (YYYY-MM-DD)
        formattedDate = moment(dateValue).format("DD MMM, YYYY")
      }
    } else {
      formattedDate = "Invalid Date"
    }

    // Convert duration to seconds if input is in minutes
    const convertedDurationValue =
      durationValue && typeof durationValue === "number"
        ? timeUnit === "minutes"
          ? durationValue * 60
          : durationValue
        : durationValue

    // Assign color based on duration comparison (in seconds)
    const color =
      convertedDurationValue === maxValue && maxValue > 0 ? "var(--blue-background-1)" : "var(--blue-background-2)"

    return {
      ...visit,
      [durationField]: convertedDurationValue,
      formattedDate,
      color,
    }
  })
}

/**
 * Converts a given time to the total minutes from the start of the day.
 *
 * @param {string} time - The time string to convert, formatted as an ISO 8601 string (e.g., '2024-09-24T14:32:19').
 * @returns {number} - The total number of minutes from 12:00 AM (start of the day) to the given time.
 *
 * @example
 * // 14:32 -> 872 minutes
 */
export const convertTimeToMinutes = (time: string): number => {
  const timeMoment = moment.parseZone(time) // Use parseZone to preserve the input timezone
  const startOfDay = timeMoment.clone().startOf("day")
  return timeMoment.diff(startOfDay, "minutes")
}
/**
 * Function to convert data to CSV and trigger download
 * @param {TableColumn[]} columns - The columns of the table
 * @param {any[]} data - The data to be exported
 */
export const exportTableDataToCSV = (columns: TableColumn[], data: any[], title?: string) => {
  const csvRows = []

  // Get headers
  const headers = columns.map((col) => col.title)
  csvRows.push(headers.join(","))

  // Loop over the rows
  for (const row of data) {
    const values = columns.map((col) => {
      const value = row[col.field]
      return typeof value === "string" ? `"${value.replace(/"/g, '""')}"` : value
    })
    csvRows.push(values.join(","))
  }

  // Create a blob and trigger download
  const csvString = csvRows.join("\n")
  const blob = new Blob([csvString], { type: "text/csv" })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.setAttribute("hidden", "")
  a.setAttribute("href", url)
  a.setAttribute("download", title ? `${title}.csv` : "table_data.csv")
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/**
 * Exports fetched data to a CSV file and triggers a download.
 * This function is used to export data from the backend to a CSV file.
 *
 * @param {string} data - The data to be exported, formatted as a CSV string.
 * @param {string} filename - The name of the file to be downloaded (without the .csv extension).
 */
export const exportFetchedDataToCSV = (data: string, filename: string) => {
  const blob = new Blob([data], { type: "text/csv;charset=utf-8;" })
  const link = document.createElement("a")
  const url = URL.createObjectURL(blob)

  link.setAttribute("href", url)
  link.setAttribute("download", `${filename}.csv`)
  link.style.visibility = "hidden"

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * Formats a given date or timestamp string for display in a table based on the specified time grain.
 *
 * @param {Date | string} [date] - The date or timestamp string to format. If not provided, returns undefined.
 * @param {string} [timeGrain] - The time grain to format the date for. Can be "hour" or any other value. Defaults to a more detailed format if not "hour".
 * @returns {string} - The formatted date string.
 */
export const formatTimestampForTable = (date?: Date | string, timeGrain?: string) => {
  if (!date) return
  const options: DateTimeFormatOptions =
    timeGrain === "hour"
      ? { hour12: true, hour: "2-digit" as const }
      : timeGrain === "month"
      ? { month: "short", year: "numeric" }
      : { hour12: true, weekday: "short", day: "numeric", month: "short" }

  return new Date(date).toLocaleString("en-US", options)
}
/**
 * Function to get the initial active stats tab based on the screen size and the presence of graph and table props.
 * @param {Object} options - The options for the function.
 * @param {any} options.graphProps - The graph props.
 * @param {any} options.tableProps - The table props.
 * @param {boolean} options.xSmallScreen - Whether the screen is extra small.
 * @returns {number} The initial active stats tab.
 */
export const getInitialActiveStatsTab = ({
  graphProps,
  tableProps,
  xSmallScreen,
}: {
  graphProps?: any
  tableProps?: any
  xSmallScreen?: boolean
}): number => {
  if (graphProps && tableProps) {
    return xSmallScreen ? 1 : 0
  } else if (graphProps) {
    return 0
  } else if (tableProps) {
    return 1
  }
  return 0
}

/**
 * Parses a duration string into an object with hours, minutes, and seconds.
 *
 * @param {string} duration - The duration string to parse (format "HH:mm:ss").
 * @returns {object} - An object with hours, minutes, and seconds.
 */
const parseDuration = (duration: string): { hours: number; minutes: number; seconds: number } => {
  const [hours, minutes, seconds] = duration.split(":").map(Number)
  return { hours, minutes, seconds }
}

/**
 * Converts a duration string in the format "HH:mm:ss" to the total number of seconds.
 * If the duration is undefined or results in NaN, it returns 0.
 *
 * @param {string} duration - The duration string to convert (format "HH:mm:ss").
 * @returns {number} - The total number of seconds.
 */
export function visitDurationToSeconds(duration?: string): number {
  if (!duration) {
    return 0
  }

  const { hours, minutes, seconds } = parseDuration(duration)
  const totalSeconds = hours * 60 * 60 + minutes * 60 + seconds
  return isNaN(totalSeconds) ? 0 : totalSeconds
}

export const ServiceEventTypeMapper: Record<number, ServiceEventType> = {
  0: "fire",
  1: "violence",
  2: "motion",
  3: "intrusion",
  4: "blackListCar",
  5: "blackListPerson",
  6: "stray_animals",
  7: "ppe",
}

/**
 * Canonical UI service keys used across the application.
 * - UI-level names that appear in filters and forms
 * - Some license feature keys map to these (e.g., "cars" -> "car")
 * - Ordered for consistent rendering
 */
export const ALL_SERVICE_KEYS = [
  "mcp",
  "heatmap",
  "counter",
  "occupancy",
  "face",
  "violence",
  "motion",
  "intrusion",
  "fire",
  "stray_animals",
  "ppe",
  "car",
  "defect_detection",
] as const

export type ServiceKey = (typeof ALL_SERVICE_KEYS)[number]

/**
 * Static UI grouping of services. This is filtered by availability at runtime.
 */
export const SERVICE_GROUPS_UI: { title: string; keys: ServiceKey[] }[] = [
  { title: "People Service", keys: ["mcp", "heatmap", "counter", "face", "occupancy"] },
  { title: "Security Service", keys: ["violence", "motion", "intrusion", "fire", "stray_animals", "ppe"] },
  { title: "Car Service", keys: ["car"] },
  { title: "Industrial Service", keys: ["defect_detection"] },
]

/**
 * Backend license features grouped by domain. Not 1:1 with UI keys.
 */
const SERVICE_GROUPS_LICENSE = {
  analytics: ["mcp", "heatmap", "counter", "occupancy"] as const,
  security: ["violence", "motion", "intrusion", "fire", "stray_animals", "ppe", "cars", "face"] as const,
  industrial: ["defect_detection"] as const,
} as const

/**
 * License feature to UI service key mapping where names differ.
 */
const FEATURE_KEY_TO_SERVICE_KEY: Record<string, ServiceKey> = {
  cars: "car",
} as const

/** Services availability map keyed by UI ServiceKey. */
export type CameraServices = Partial<Record<ServiceKey, boolean>>

/**
 * Derive available UI service keys from license features at runtime.
 * Returned as a Set to represent presence-only availability.
 */
export function deriveAvailableServiceKeys(licenseFeatures?: Features | undefined): ReadonlySet<ServiceKey> {
  const keys = new Set<ServiceKey>()
  if (!licenseFeatures) return keys
  Object.entries(SERVICE_GROUPS_LICENSE).forEach(([group, featureKeys]) => {
    const licenseGroupFeatures = licenseFeatures[group as keyof Features]
    if (!licenseGroupFeatures) return
    featureKeys.forEach((featureKey) => {
      if (featureKey in licenseGroupFeatures) {
        const featureValue = licenseGroupFeatures[featureKey as keyof typeof licenseGroupFeatures]
        if (featureValue === true) {
          const mapped = FEATURE_KEY_TO_SERVICE_KEY[featureKey as string]
          const serviceKey = (mapped ?? (featureKey as ServiceKey)) as ServiceKey
          keys.add(serviceKey)
        }
      }
    })
  })
  return keys
}

/**
 * Masks sensitive credentials (username and password) in streaming URLs.
 *
 * This function is designed to handle a wide range of camera stream URL formats,
 * including:
 * - Standard `user:pass@host` (e.g., rtsp://admin:12345@192.168.1.1:554/)
 * - Passwords containing `@` symbols (e.g., rtsp://admin:pa@ss@host)
 * - Embedded credentials like `user=admin_password=123456_channel=1`
 * - Any protocol (not just RTSP/HTTP) — e.g., ftp://, customproto://, etc.
 *
 * ---
 * ### Supported Patterns (based on real-world camera RTSP templates):
 *
 * ```ts
 * const STREAM_TEMPLATES = [
 *   "rtsp://{username}:{password}@{ip}:554/Streaming/Channels/{channel}01",     // Hikvision
 *   "rtsp://{username}:{password}@{ip}:554/cam/realmonitor?channel={channel}",  // Dahua / General
 *   "rtsp://{username}:{password}@{ip}:554/unicast/c{channel}/s1/live",         // Unicast
 *   "rtsp://{ip}:554/user={username}_password={password}_channel={channel}",    // Chinese-style
 *   "rtsp://{username}:{password}@{ip}:554/axis-media/media.amp",               // Axis
 *   "rtsp://{username}:{password}@{ip}:554/live/{channel}",                     // Generic
 *   "rtsp://{username}:{password}@{ip}:554/stream{channel}",                    // Generic
 *   "rtsp://{username}:{password}@{ip}/media/video{channel}",                   // Chinese-style
 *   "rtsp://{username}:{password}@{ip}:554/uniview/stream/{channel}",           // Uniview / Zmodo
 *   "rtsp://{username}:{password}@{ip}:554/play{channel}",                      // Older devices
 *   "rtsp://{username}:{password}@{ip}:554/rec/{channel}",                      // Older devices
 *   "rtsp://{username}:{password}@{ip}:554/profile{channel}/media.smp",         // Samsung
 *   "rtsp://{username}:{password}@{ip}:554/rtsp_tunnel",                        // Bosch
 *   "rtsp://{username}:{password}@{ip}:554/{channel}",                          // Less common
 *   "rtsp://{username}:{password}@{ip}:554/video{channel}",                     // Less common
 * ];
 * ```
 *
 * ---
 * ### Examples:
 *
 * ```ts
 * maskUrl("rtsp://admin:12345@192.168.1.1:554/")
 * // => "rtsp://*****:*****@192.168.1.1:554/"
 *
 * maskUrl("rtsp://192.168.1.1:554/user=admin_password=123456_channel=1")
 * // => "rtsp://192.168.1.1:554/user=*****_password=*****_channel=1"
 *
 * maskUrl("https://admin:Fetra@123456@10.30.4.106:554/Streaming/Channels/11")
 * // => "https://*****:*****@10.30.4.106:554/Streaming/Channels/11"
 *
 * maskUrl("ftp://user:pa@ss@host:21/file")
 * // => "ftp://*****:*****@host:21/file"
 * ```
 *
 * @param url - The original URL possibly containing credentials.
 * @returns The masked URL with sensitive credentials hidden.
 */
export const maskUrl = (url: string): string => {
  let masked = url

  // 1. Mask embedded credentials like: user=admin_password=123456_channel=1
  // These occur in some Chinese camera URLs
  masked = masked.replace(
    /(user=)([^_]+)(_password=)([^_]+)([_\w=]*)/i,
    (_match, p1, _user, p3, _pass, rest) => `${p1}*****${p3}*****${rest}`
  )

  // 2. Mask standard user:password@host patterns
  // Match the scheme (any protocol) followed by the rest of the URL
  const protocolMatch = masked.match(/^([a-z][a-z0-9+\-.]*):\/\//i)
  if (protocolMatch) {
    const protocol = protocolMatch[0] // e.g., "rtsp://", "ftp://", etc.
    const rest = masked.slice(protocol.length) // Everything after the protocol

    // Find the last @ symbol (to handle passwords that contain @)
    const atIndex = rest.lastIndexOf("@")
    if (atIndex !== -1) {
      const credentialsSection = rest.slice(0, atIndex) // Everything before the last @
      const hostAndPath = rest.slice(atIndex + 1) // Everything after the last @

      // Only proceed if it looks like user:password format
      const colonIndex = credentialsSection.indexOf(":")
      if (colonIndex !== -1) {
        masked = `${protocol}*****:*****@${hostAndPath}`
      }
    }
  }

  return masked
}

/**
 * Exports graph components as images and bundles them into a ZIP file.
 *
 * Supports both history and live modes:
 * - History: provide both `startDate` and `endDate` → filename will be
 *   "<prefix> - From <start> To <end>.zip"
 * - Live: provide only `startDate` (e.g., now) → filename will be
 *   "<prefix> in day <date>.zip"
 *
 * @param refs - A list of React refs pointing to graph container elements.
 * @param startDate - Start date (or the single date for live mode).
 * @param endDate - Optional end date. If omitted, live mode is assumed.
 * @param fileNamePrefix - Optional prefix for the exported file name.
 * @param setIsExportLoading - Optional function to set loading state during export process.
 */
export const exportGraphsAsImages = async ({
  refs,
  startDate,
  endDate,
  fileNamePrefix = "Analytics",
  setIsExportLoading,
}: {
  refs: React.RefObject<HTMLElement>[]
  startDate: moment.Moment
  endDate?: moment.Moment
  fileNamePrefix?: string
  setIsExportLoading?: (loading: boolean) => void
}): Promise<void> => {
  if (!startDate) return

  // Set loading state to true when export starts
  setIsExportLoading?.(true)

  const zip = new JSZip()

  try {
    // Convert all refs to images in parallel, preserving order
    const images = await Promise.all(
      refs.map(async (ref, index) => {
        if (!ref.current) return null

        // Wait for charts to fully render and animate
        await waitForChartsToRender(ref.current)

        const blob = await domtoimage.toBlob(ref.current, {
          cacheBust: true,
          // Ensure high quality capture
          quality: 1.0,
        })

        if (!blob) return null

        // Graph title fallback: use data-title if set, otherwise fallback to index
        const title = ref.current.getAttribute("data-title") ?? `graph-${index + 1}`

        return { blob, title }
      })
    )

    // Add non-null images to ZIP
    images.forEach((img) => {
      if (img) {
        zip.file(`${img.title}.jpeg`, img.blob, { binary: true })
      }
    })

    // Generate and download the ZIP
    const content = await zip.generateAsync({ type: "blob" })
    const fileName = endDate
      ? `${fileNamePrefix} - From ${startDate.format("DD-MM-YYYY hh:mm A")} To ${endDate.format(
          "DD-MM-YYYY hh:mm A"
        )}.zip`
      : `${fileNamePrefix} in day ${startDate.format("DD-MM-YYYY")} at ${startDate.format("hh:mm A")}.zip`
    FileSaver.saveAs(content, fileName)
  } catch (error) {
    NotificationUtils.toast("Error exporting graphs", { severity: "error" })
  } finally {
    // Set loading state to false when export completes (success or error)
    setIsExportLoading?.(false)
  }
}

/**
 * Waits for charts to fully render and animate before capturing.
 * This ensures all SVG elements (lines, bars, colors) are visible.
 *
 * @param element - The DOM element containing the chart
 */
const waitForChartsToRender = async (element: HTMLElement): Promise<void> => {
  return new Promise((resolve) => {
    // Wait for initial render
    setTimeout(() => {
      // Check if there are SVG elements that need time to render
      const svgElements = element.querySelectorAll("svg")

      if (svgElements.length === 0) {
        // No SVG charts, resolve immediately
        resolve()
        return
      }

      // Wait for SVG animations to complete
      // Nivo charts typically take 300-500ms to animate
      const animationDelay = 800 // Conservative delay to ensure completion

      setTimeout(() => {
        // Additional check for any ongoing animations
        const animatedElements = element.querySelectorAll('[style*="animation"], [style*="transition"]')

        if (animatedElements.length === 0) {
          resolve()
        } else {
          // Wait a bit more for any remaining animations
          setTimeout(resolve, 200)
        }
      }, animationDelay)
    }, 100) // Small initial delay for DOM updates
  })
}

/**
 * Converts a date string (e.g., "2025-08-01") to a human-readable format (e.g., "1 Aug 2025").
 * @param dateStr - The date string to format.
 * @returns The formatted date string.
 */
const formatDateToReadable = (dateStr: string): string => {
  // moment parses ISO dates by default
  return moment(dateStr).format("D MMM YYYY")
}

/**
 * Processes an array of objects, converting the value of a specified field to a readable date or hour format.
 * @param data - The array of objects to process.
 * @param key - The key whose value should be formatted.
 * @param isHours - Optional. If true, formats as hour (e.g., "10 PM"); otherwise, as date (e.g., "1 Aug 2025").
 * @returns The processed array with formatted field values.
 */
export function processArrayFieldToReadableDate<T extends Record<string, any>>(
  data: T[],
  key: string,
  isHours?: boolean
): T[] {
  return data.map((item) => {
    const value = item[key]
    if (value == null) return item
    let formattedValue: string
    if (isHours) {
      formattedValue = formatHourlyTime(value, "h A")
    } else {
      formattedValue = formatDateToReadable(value)
    }
    return {
      ...item,
      [key]: formattedValue,
    }
  })
}

/**
 * Converts a Duration object to total milliseconds
 * @param duration - The Duration object from date-fns
 * @returns The total number of milliseconds represented by the duration
 */
export const durationToMilliseconds = (duration: Duration): number => {
  const days = duration.days || 0
  const hours = duration.hours || 0
  const minutes = duration.minutes || 0
  const seconds = duration.seconds || 0

  return days * 24 * 60 * 60 * 1000 + hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000
}

/**
 * Checks if a Duration object is empty (all time components are zero or undefined)
 * @param duration - The Duration object from date-fns
 * @returns true if the duration represents zero time, false otherwise
 */
export const isDurationEmpty = (duration: Duration): boolean => {
  return durationToMilliseconds(duration) === 0
}

/**
 * Creates a params serializer function for axios that handles arrays and repeated parameters
 * @param repeatedParams - Optional object where keys are parameter names and values are arrays to append as repeated parameters.
 *                          For backward compatibility, can also accept a number array which will be treated as "branches".
 * @returns A function that serializes params to URLSearchParams string format
 */
export const createParamsSerializer = (
  repeatedParams?: Record<string, (string | number)[]> | number[]
): ((params: any) => string) => {
  // Handle backward compatibility: if first arg is an array, treat it as branches
  const normalizedRepeatedParams: Record<string, (string | number)[]> = Array.isArray(repeatedParams)
    ? { branches: repeatedParams }
    : repeatedParams || {}

  return (params: any): string => {
    const searchParams = new URLSearchParams()
    // Add regular params
    Object.keys(params).forEach((key) => {
      const value = params[key]
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // For arrays, add each value as a repeated parameter
          value.forEach((item) => searchParams.append(key, String(item)))
        } else {
          searchParams.append(key, String(value))
        }
      }
    })
    // Add repeated parameters from the repeatedParams object
    Object.keys(normalizedRepeatedParams).forEach((paramName) => {
      const paramValues = normalizedRepeatedParams[paramName]
      if (paramValues && paramValues.length > 0) {
        paramValues.forEach((value) => searchParams.append(paramName, String(value)))
      }
    })
    return searchParams.toString()
  }
}

/**
 * Generic sorting function that handles invalid/zero values by pushing them to the end.
 *
 * @template T - The type of items being sorted
 * @param {T[]} itemsToSort - The array of items to sort
 * @param {keyof T | ((item: T) => number | null | undefined)} sortFieldOrExtractor - The field name to sort by, or a function that extracts the numeric value from each item
 * @param {Object} sortOptions - Sorting configuration options
 * @param {"asc" | "desc"} [sortOptions.direction="asc"] - Sort direction: "asc" for ascending (lowest first), "desc" for descending (highest first)
 * @param {boolean} [sortOptions.treatZeroAsInvalid=false] - Whether to treat 0 as an invalid value (push to end). Useful for metrics where 0 indicates missing data
 * @returns {T[]} - The sorted array with invalid values (null, undefined, or optionally 0) placed at the end
 */
export function sortWithInvalidAtEnd<T>(
  itemsToSort: T[],
  sortFieldOrExtractor: keyof T | ((item: T) => number | null | undefined),
  sortOptions: {
    direction?: "asc" | "desc"
    treatZeroAsInvalid?: boolean
  } = {}
): T[] {
  const { direction = "asc", treatZeroAsInvalid = false } = sortOptions

  // Extract numeric values from items using either field name or custom extractor function
  // Extracted outside sort callback for better performance
  const extractNumericValue = (item: T): number | null | undefined => {
    if (typeof sortFieldOrExtractor === "function") {
      return sortFieldOrExtractor(item)
    }
    return (item[sortFieldOrExtractor] as number | null | undefined) ?? null
  }

  // Determine if a value should be considered valid for sorting
  // Extracted outside sort callback for better performance
  const isValueValidForSorting = (numericValue: number | null | undefined): boolean => {
    if (numericValue == null) return false
    if (treatZeroAsInvalid && numericValue === 0) return false
    return true
  }

  return [...itemsToSort].sort((firstItem, secondItem) => {
    const firstItemValue = extractNumericValue(firstItem)
    const secondItemValue = extractNumericValue(secondItem)

    const isFirstItemValueValid = isValueValidForSorting(firstItemValue)
    const isSecondItemValueValid = isValueValidForSorting(secondItemValue)

    // If both items have invalid values, maintain their original order
    if (!isFirstItemValueValid && !isSecondItemValueValid) return 0

    // If only the first item has an invalid value, place it after the second item
    if (!isFirstItemValueValid) return 1

    // If only the second item has an invalid value, place it after the first item
    if (!isSecondItemValueValid) return -1

    // Both items have valid values, sort them based on the specified direction
    // Type assertion is safe here because we've already validated both values are valid
    const firstItemNumericValue = firstItemValue as number
    const secondItemNumericValue = secondItemValue as number

    if (direction === "asc") {
      return firstItemNumericValue - secondItemNumericValue
    } else {
      return secondItemNumericValue - firstItemNumericValue
    }
  })
}
